import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { BookOpen, Download, Gift, Loader2, Music, Play, Sparkles, Ticket } from "lucide-react";
import { getMyLibrary, redeemAccessCode, requestDownload, type LibraryPayload } from "@/lib/ownership.functions";
import { formatMoney, type OwnedItem } from "@/lib/ownership";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { track } from "@/lib/analytics";

export const Route = createFileRoute("/_authenticated/library")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Your Library · KAfrequency" },
      {
        name: "description",
        content: "Everything you own on KAfrequency — books, albums, activations, and courses, with secure downloads.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: LibraryPage,
});

const TABS = [
  { id: "all", label: "All" },
  { id: "book", label: "Books" },
  { id: "album", label: "Music" },
  { id: "activation", label: "Activations" },
  { id: "course", label: "Courses" },
] as const;

function iconFor(item: OwnedItem) {
  if (item.productType === "album" || item.productType === "single") return Music;
  if (item.productType === "activation") return Sparkles;
  return BookOpen;
}

function LibraryPage() {
  const load = useServerFn(getMyLibrary);
  const download = useServerFn(requestDownload);
  const redeem = useServerFn(redeemAccessCode);

  const [data, setData] = useState<LibraryPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<(typeof TABS)[number]["id"]>("all");
  const [busy, setBusy] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      setData(await load({ data: {} }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load your library.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = useMemo(() => {
    const all = data?.items ?? [];
    if (tab === "all") return all;
    if (tab === "album") return all.filter((i) => i.productType === "album" || i.productType === "single");
    return all.filter((i) => i.productType === tab);
  }, [data, tab]);

  async function onDownload(item: OwnedItem) {
    setBusy(item.slug);
    try {
      const res = await download({ data: { slug: item.slug } });
      if ("error" in res) {
        toast.error(res.error);
        return;
      }
      track("download_requested", { product_slug: item.slug, files: res.files.length });
      for (const file of res.files) window.open(file.url, "_blank", "noopener,noreferrer");
      toast.success(res.files.length > 1 ? `${res.files.length} files ready` : "Your download is ready");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Download failed");
    } finally {
      setBusy(null);
    }
  }

  async function onRedeem(event: React.FormEvent) {
    event.preventDefault();
    if (!code.trim()) return;
    setRedeeming(true);
    try {
      const res = await redeem({ data: { code } });
      if ("error" in res) {
        toast.error(res.error);
        return;
      }
      toast.success(`Unlocked ${res.granted.length} item${res.granted.length === 1 ? "" : "s"}`);
      setCode("");
      await refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not redeem that code");
    } finally {
      setRedeeming(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <header className="mb-8">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Digital Ownership</p>
        <h1 className="mt-2 font-serif text-3xl text-gold-luminous sm:text-4xl">Your Library</h1>
        <p className="mt-3 max-w-xl text-sm text-muted-foreground">
          Everything you own, in one place. Purchases stay yours permanently; membership access stays active while your
          membership is active.
        </p>
      </header>

      <nav aria-label="Filter library" className="mb-6 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            aria-pressed={tab === t.id}
            className={`min-h-11 rounded-full px-4 text-sm transition ${
              tab === t.id
                ? "bg-gold-gradient font-semibold text-obsidian"
                : "border hairline text-muted-foreground hover:text-gold-luminous"
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="animate-spin" size={16} /> Loading your library…
        </p>
      ) : error ? (
        <div className="rounded-2xl border hairline bg-charcoal/40 p-6">
          <p className="text-sm text-rose-400">{error}</p>
          <Button className="mt-4" variant="outline" onClick={() => void refresh()}>
            Try again
          </Button>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border hairline bg-charcoal/30 p-8 text-center">
          <p className="font-serif text-2xl text-gold-luminous">Nothing here yet.</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Anything you purchase or unlock with membership appears here instantly.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/shop" className="rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian">
              Browse the shop
            </Link>
            <Link to="/membership" className="rounded-full border border-gold px-5 py-2.5 text-sm text-gold-luminous">
              See membership
            </Link>
          </div>
        </div>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {items.map((item) => {
            const Icon = iconFor(item);
            return (
              <li key={item.slug} className="rounded-2xl border hairline bg-charcoal/40 p-4">
                <div className="flex items-start gap-3">
                  <div className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-lg bg-obsidian">
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
                    ) : (
                      <Icon size={16} className="text-gold" aria-hidden />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{item.title}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {item.source === "membership"
                        ? "Included with membership"
                        : item.source === "bundle"
                          ? `Included in ${item.viaBundle ?? "a bundle"}`
                          : item.source === "code"
                            ? "Unlocked with an access code"
                            : item.source === "admin"
                              ? "Granted by KAfrequency"
                              : "Purchased"}
                      {item.grantedAt ? ` · ${new Date(item.grantedAt).toLocaleDateString()}` : ""}
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {item.downloadable && item.source !== "membership" && (
                    <Button
                      size="sm"
                      onClick={() => void onDownload(item)}
                      disabled={busy === item.slug}
                      className="min-h-11"
                    >
                      {busy === item.slug ? (
                        <Loader2 className="animate-spin" size={14} />
                      ) : (
                        <Download size={14} aria-hidden />
                      )}
                      Download
                    </Button>
                  )}
                  {item.streamable && (
                    <Button asChild size="sm" variant="outline" className="min-h-11">
                      <Link to="/shop/$slug" params={{ slug: item.slug }}>
                        <Play size={14} aria-hidden /> Open
                      </Link>
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <section className="mt-10 rounded-2xl border hairline bg-charcoal/40 p-5">
        <h2 className="flex items-center gap-2 font-serif text-xl text-gold-luminous">
          <Ticket size={16} className="text-gold" aria-hidden /> Redeem an access code
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Have a gift or partner code? Enter it to add the content to your library.
        </p>
        <form onSubmit={onRedeem} className="mt-4 flex flex-wrap gap-2">
          <label htmlFor="access-code" className="sr-only">
            Access code
          </label>
          <Input
            id="access-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="KAF-XXXXXX"
            className="min-h-11 max-w-xs"
            autoComplete="off"
          />
          <Button type="submit" disabled={redeeming || !code.trim()} className="min-h-11">
            {redeeming ? <Loader2 className="animate-spin" size={14} /> : <Gift size={14} aria-hidden />} Redeem
          </Button>
        </form>
      </section>

      {data && data.orders.length > 0 && (
        <section className="mt-10">
          <h2 className="font-serif text-xl text-gold-luminous">Order history</h2>
          <div className="mt-3 overflow-x-auto rounded-2xl border hairline">
            <table className="w-full min-w-[560px] text-left text-sm">
              <caption className="sr-only">Your KAfrequency orders</caption>
              <thead className="bg-obsidian/60 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th scope="col" className="px-4 py-3">Order</th>
                  <th scope="col" className="px-4 py-3">Item</th>
                  <th scope="col" className="px-4 py-3">Total</th>
                  <th scope="col" className="px-4 py-3">Status</th>
                  <th scope="col" className="px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.orders.map((order) => (
                  <tr key={order.id} className="border-t hairline">
                    <td className="px-4 py-3 text-muted-foreground">{order.orderNumber ?? order.id.slice(0, 8)}</td>
                    <td className="px-4 py-3 text-foreground">{order.productTitle}</td>
                    <td className="px-4 py-3">{formatMoney(order.amountCents, order.currency)}</td>
                    <td className="px-4 py-3">
                      <span className="capitalize text-muted-foreground">
                        {order.refundedAt ? "refunded" : (order.fulfillmentStatus ?? order.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
